import { createContext, useContext } from 'react'
import { Locale, ProfileDataFragment, Role } from '../generated/graphql'
import { EntityNames } from '../helpers/DataHelper'
import i18n from '../i18n'

export enum AclAction {
  create = 'c',
  read = 'r',
  update = 'u',
  delete = 'd',
}

export interface CanBeInterface {
  canBeUpdated: boolean
  canBeDeleted: boolean
}

export interface CanBeNodeInterface extends CanBeInterface {
  id?: string
  __typename: string
}

export interface OwnedApiNodeInterface extends CanBeNodeInterface {
  id?: string
  createdAt?: number
  updatedAt?: number
  user?: { id: string; name: string }
  creator?: { id: string; name: string }
  branch?: { name: string; id: string }
  shareWithChildBranches?: boolean
}

export class User {
  readonly viewer?: ProfileDataFragment | null
  private readonly refetch?: () => any

  constructor(viewer?: ProfileDataFragment | null, refetch?: () => any) {
    this.viewer = viewer
    this.refetch = refetch
  }

  get id(): string | undefined {
    return this.viewer?.id
  }

  canDo(entityName: EntityNames, action: AclAction | AclAction[]): boolean {
    if (this.viewer && this.viewer.permissions) {
      const p = this.viewer.permissions
      const er = p.find((pi) => pi.entity === entityName)
      if (er) {
        if (Array.isArray(action)) {
          return action.reduce((can, singleAction) => {
            return can || er[singleAction]
          }, false as boolean)
        }
        return er[action]
      }
    }
    return false
  }

  can(entity: CanBeNodeInterface, action: AclAction): boolean {
    switch (action) {
      case AclAction.create:
      case AclAction.read:
        return this.canDo(entity.__typename as EntityNames, action)
      case AclAction.update:
        return entity.canBeUpdated
      case AclAction.delete:
        return entity.canBeDeleted
    }
    return false
  }

  getTitle(): string {
    let title = ''
    if (this.viewer) {
      title = this.viewer.name
      if (this.viewer.organization) {
        title += ' / ' + this.viewer.organization.name
      }
    }
    return title
  }

  getName(): string | undefined {
    return this.viewer?.name
  }

  getEmail(): string | undefined {
    return this.viewer?.email
  }

  getTz(): string | undefined {
    return this.viewer?.tzname
  }

  isOrganization(): boolean {
    return !!this.viewer && !!(this.viewer.organization || this.viewer.isOrganization)
  }

  isUser() {
    return !!this.viewer && this.viewer.role === Role.User
  }

  refresh() {
    if (this.refetch) {
      this.refetch()
    }
  }

  locale(): Locale | undefined {
    if (this.viewer) {
      return this.viewer.locale || (i18n.language as Locale)
    }
    return i18n.language as Locale
  }
}

export const UserContext = createContext<User>(new User())

export const useUserContext = () => useContext(UserContext)
